import { useState, type ReactNode } from 'react';
import { Button, Dialog, DialogContent, DialogFooter, Field, Textarea } from '@vwt/ui';
import { useTranslation } from '../lib/i18n.js';

/**
 * Asks for an audited reason before a consequential administrative action. Replaces browser
 * prompts so the flow is accessible, localized and testable.
 */
export function ReasonDialog({ title, description, confirmLabel, destructive, onConfirm, onClose }: { title?: string; description?: string; confirmLabel?: ReactNode; destructive?: boolean; onConfirm: (reason: string) => Promise<void>; onClose: () => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={title ?? t('admin.reason.title')} description={description ?? t('admin.reason.hint')} closeLabel={t('common.close')}>
        <form
          className="flex flex-col gap-3"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!reason.trim()) return;
            setBusy(true);
            try {
              await onConfirm(reason.trim());
              onClose();
            } finally {
              setBusy(false);
            }
          }}
        >
          <Field label={t('admin.reason.label')} htmlFor="reason-input" required>
            <Textarea id="reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500} autoFocus />
          </Field>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant={destructive ? 'danger' : 'primary'} loading={busy} disabled={!reason.trim()}>
              {confirmLabel ?? t('admin.reason.confirm')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
